import { onScopeDispose } from 'vue'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import createSharedComposable from '@nicolaudie-group/ng-ui-kit/helpers/createSharedComposable'

const namespaces = new Map()

/**
 * Namespace a composable
 * Basically means that the composable will be run only the first time
 * it's called from a namespace, and it's result will be available to subsequent
 * calls of the same composable by using the same namespace
 * @template {Function} T
 * @param {String|Symbol} id - Namespace ID
 * @param {T} composable - Composable to namespace
 * @returns {T} Namespaced composable to be run
 */
function useNamespace(id, composable) {
  const providedName = composable?.name

  // If there already is a namespace with this ID
  if (namespaces.has(id)) {
    const namespace = namespaces.get(id)
    // If the composable provided doesn't match the
    // composable used when the namespace was created, show a warning
    if (namespace.composable !== composable) {
      const originalName = namespace.composable?.name
      dev.warn(new Error(`Different composable implementations ("${originalName}" & "${providedName}") have been provided for namespace "${id}".

If the behavior of those two composables is different, it will cause inconsistencies in your application depending on which one is called first.

"${originalName}" is currently used, as it was the first one to be called.`))
    }
    // Return the shared composable function
    return namespace.fn
  }

  // If there is no namespace with this ID yet, create one
  const namespaceFn = createSharedComposable((...args) => {
    // When there are no more subscribers (composables) in a namespace,
    // delete the namespace from the map
    onScopeDispose(() => { namespaces.delete(id) })
    return composable(...args)
  }, `${providedName}_${String(id)}`)

  namespaces.set(id, {
    composable,
    fn: namespaceFn,
  })

  return namespaceFn
}

export default useNamespace
